
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface AreaChartProps {
    data: number[];
    className?: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const AreaChart: React.FC<AreaChartProps> = ({ data, className }) => {
    const [tooltip, setTooltip] = useState<{ x: number; y: number; value: number; month: string } | null>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    if (!data || data.length === 0) {
        return <div className={`w-full h-full flex items-center justify-center text-[10px] text-gray-400 uppercase tracking-widest ${className}`}>No telemetry data</div>;
    }

    // Minimized padding to maximize card usage
    const PADDING = { top: 10, right: 10, bottom: 25, left: 35 };
    const SVG_WIDTH = 500;
    const SVG_HEIGHT = 250;
    const CHART_WIDTH = SVG_WIDTH - PADDING.left - PADDING.right;
    const CHART_HEIGHT = SVG_HEIGHT - PADDING.top - PADDING.bottom;

    const maxVal = Math.max(...data) * 1.05; 
    const yAxisLabels = useMemo(() => {
        const ticks = 4;
        const interval = Math.ceil(maxVal / (ticks - 1));
        return Array.from({ length: ticks }, (_, i) => i * interval);
    }, [maxVal]);
    const maxYAxis = yAxisLabels[yAxisLabels.length - 1];

    const points = data.map((p, i) => {
        const x = PADDING.left + i * (CHART_WIDTH / (data.length - 1));
        const y = PADDING.top + CHART_HEIGHT - (p / maxYAxis) * CHART_HEIGHT;
        return { x, y, value: p };
    });

    const generateSmoothPath = (pts: {x:number, y:number}[]) => {
        if (pts.length === 0) return "";
        let d = `M ${pts[0].x} ${pts[0].y}`;
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[i === 0 ? 0 : i - 1];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[i + 2] || p2;

            const cp1x = p1.x + (p2.x - p0.x) / 6;
            const cp1y = p1.y + (p2.y - p0.y) / 6;

            const cp2x = p2.x - (p3.x - p1.x) / 6;
            const cp2y = p2.y - (p3.y - p1.y) / 6;

            d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }
        return d;
    };

    const path = generateSmoothPath(points);
    const areaPath = `${path} L ${points[points.length-1].x} ${CHART_HEIGHT + PADDING.top} L ${PADDING.left} ${CHART_HEIGHT + PADDING.top} Z`;

    const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
        if (!svgRef.current) return;
        const svg = svgRef.current;
        const rect = svg.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const scaleX = SVG_WIDTH / rect.width;
        const svgX = x * scaleX;
        
        const index = Math.round((svgX - PADDING.left) / (CHART_WIDTH / (data.length - 1)));

        if (index >= 0 && index < data.length) {
            setTooltip({
                ...points[index],
                month: MONTHS[index % MONTHS.length],
            });
        }
    };

    return (
        <div className={`w-full h-full relative ${className}`}>
            <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)} className="overflow-visible select-none">
                <defs>
                    <linearGradient id="area-chart-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C5A059" stopOpacity={0.2}/>
                        <stop offset="100%" stopColor="#C5A059" stopOpacity={0}/>
                    </linearGradient>
                </defs>

                {/* Y-Axis Grid & Labels */}
                {yAxisLabels.map((label, i) => {
                    const y = PADDING.top + CHART_HEIGHT - (label / maxYAxis) * CHART_HEIGHT;
                    return (
                        <g key={i}>
                            <line x1={PADDING.left} y1={y} x2={SVG_WIDTH} y2={y} stroke="currentColor" className="text-stone-100 dark:text-white/[0.03]" strokeWidth="1" />
                            <text x={PADDING.left - 6} y={y + 3} textAnchor="end" className="text-[8px] fill-stone-400 font-mono font-bold tracking-tighter">
                                {label >= 1000 ? `${(label/1000).toFixed(1)}k` : label}
                            </text>
                        </g>
                    );
                })}

                {/* X-Axis Labels */}
                {points.map((p, i) => i % 2 === 0 && (
                    <text key={i} x={p.x} y={SVG_HEIGHT - 6} textAnchor="middle" className="text-[8px] fill-stone-400 font-mono font-black uppercase tracking-tighter">
                        {MONTHS[i % MONTHS.length]}
                    </text>
                ))}

                <motion.path d={areaPath} fill="url(#area-chart-gradient)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} />
                <motion.path d={path} fill="none" stroke="#C5A059" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.8, ease: "easeOut" }} />

                <AnimatePresence>
                    {tooltip && (
                        <g>
                            <line x1={tooltip.x} y1={PADDING.top} x2={tooltip.x} y2={CHART_HEIGHT + PADDING.top} stroke="#C5A059" strokeWidth="0.5" strokeDasharray="2 2" className="opacity-40" />
                            <circle cx={tooltip.x} cy={tooltip.y} r="4" fill="#C5A059" stroke="white" strokeWidth="2" className="drop-shadow-sm" />
                            <g transform={`translate(${Math.min(Math.max(tooltip.x - 40, 0), SVG_WIDTH - 80)}, ${PADDING.top})`}>
                                <rect width="80" height="35" rx="2" fill="#0A0B0F" stroke="#2D3142" strokeWidth="0.5" className="shadow-xl" />
                                <text x="40" y="14" textAnchor="middle" fill="#9ca3af" className="text-[8px] font-black uppercase tracking-[0.2em]">{tooltip.month}</text>
                                <text x="40" y="27" textAnchor="middle" fill="white" className="text-xs font-mono font-black">${tooltip.value.toLocaleString()}</text>
                            </g>
                        </g>
                    )}
                </AnimatePresence>
            </svg>
        </div>
    );
};
